/**
 * SquadTvColumn — one half of the Stadium board: a fixed "Rank / Player / Pts"
 * header above a continuously auto-scrolling list. Rows are duplicated and
 * translated upward on a seamless loop (credits style) when the list overflows.
 */

import React, { useMemo, useState } from 'react';
import { Animated, View, Text } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../../src/theme/useTheme';
import { useSquadTvMarqueeScroll } from '../../../src/worldcup/hooks/useSquadTvMarqueeScroll';
import { SQUAD_TV_STADIUM_ROW_HEIGHT } from '../../../src/worldcup/constants/squadTvMode.constants';
import { SquadTvStandingRow } from './SquadTvStandingRow';
import { getSquadTvColumnStyles } from './SquadTvColumn.styles';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';

interface SquadTvColumnProps {
  entries: LeaderboardEntry[];
  autoScrollEnabled: boolean;
  /** Draws the vertical separator between the two columns. */
  showDivider?: boolean;
  testID?: string;
}

const COLUMN_COPY = { rank: 'Rank', player: 'Player', pts: 'Pts' } as const;

export const SquadTvColumn: React.FC<SquadTvColumnProps> = ({
  entries,
  autoScrollEnabled,
  showDivider = false,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getSquadTvColumnStyles(theme), [theme]);
  const [viewportHeight, setViewportHeight] = useState(0);

  const { translateY, isScrolling } = useSquadTvMarqueeScroll({
    enabled: autoScrollEnabled,
    rowCount: entries.length,
    rowHeight: SQUAD_TV_STADIUM_ROW_HEIGHT,
    viewportHeight,
  });

  return (
    <View style={[styles.column, showDivider && styles.columnDivider]} testID={testID}>
      <View style={styles.head}>
        <Text style={[styles.headLabel, styles.headRank]}>{COLUMN_COPY.rank}</Text>
        <Text style={[styles.headLabel, styles.headPlayer]}>{COLUMN_COPY.player}</Text>
        <Text style={[styles.headLabel, styles.headPts]}>{COLUMN_COPY.pts}</Text>
      </View>
      <View
        style={styles.viewport}
        onLayout={(event) => setViewportHeight(event.nativeEvent.layout.height)}
      >
        <Animated.View style={[styles.track, { transform: [{ translateY }] }]}>
          {entries.map((entry, index) => (
            <SquadTvStandingRow key={entry.id} entry={entry} index={index} />
          ))}
          {isScrolling
            ? entries.map((entry, index) => (
                <SquadTvStandingRow key={`${entry.id}-loop`} entry={entry} index={index} />
              ))
            : null}
        </Animated.View>
        {isScrolling ? (
          <LinearGradient
            colors={['transparent', theme.background.primary]}
            style={styles.bottomFade}
            pointerEvents="none"
          />
        ) : null}
      </View>
    </View>
  );
};
